import React, { useState, useRef, useCallback, useMemo, useEffect } from 'react';
import {
  Chip, Box, Button,
  Typography, 
  Card, CardContent, CardHeader, TextField, Grid, Alert,
  IconButton, Tooltip
} from '@mui/material';
import LinkIcon from '@mui/icons-material/Link';
import LinkOffIcon from '@mui/icons-material/LinkOff';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import { useTranslation } from 'react-i18next';
import TagsDropdown from '../TagsDropdown';
import ActionSettingsLayout from '../ActionSettingsHeader';
import { useActionSettings } from '../../hooks/useActionSettings';
import TagEditor from '../Widgets/TagEditor';
import { useWebSocket } from '../WebSocketContext';
import { buildGetExportSettingsPayload } from '../../Utils/actionSettingsUtils';

const ActionsSendTeamsMessage = ({ caseid, tags, settingId, onSaveStatusChange, initialScope = 'event' }) => {
  const { t } = useTranslation();
  const { ws, subscribeToMessages } = useWebSocket();
  
  const [teamId, setTeamId] = useState('');
  const [channelId, setChannelId] = useState('');
  const [messageTemplate, setMessageTemplate] = useState('');
  const [accountEmail, setAccountEmail] = useState('');
  const [connectionStatus, setConnectionStatus] = useState('disconnected'); // disconnected | pending | connected
  const [connectionMessage, setConnectionMessage] = useState('');
  const [pendingDisconnect, setPendingDisconnect] = useState(false);
  const [verified, setVerified] = useState(false);
  const [isAuthorizing, setIsAuthorizing] = useState(false);
  const [isCheckingConnection, setIsCheckingConnection] = useState(false);
  const [anchorEl, setAnchorEl] = useState(null); 
  const [menuType, setMenuType] = useState('');

  const editorRef = useRef(null);
  const teamIdRef = useRef(null);
  const channelIdRef = useRef(null);

  // Unified action settings hook
  const {
    isEnabled,
    setIsEnabled,
    conditions,
    isSaveDisabled,
    forceRunLoading,
    forceRunResult,
    popUpOpen,
    forceRunWarningOpen,
    forceRunWarningCount,
    handleSave,
    handleForceRun,
    handleForceRunConfirm,
    handleForceRunCancel,
    handleConditionsChange,
    handleCloseStatusWindow,
    id
  } = useActionSettings({
    settingId,
    caseid,
    actionType: 'msteamsSendMessage',
    onSaveStatusChange,
    initialScope,
    customComparison: (initial) => {
      const initialTeamsAuth = initial.teamsAuth || {};
      return teamId === (initial.team_id || '') &&
             channelId === (initial.channel_id || '') &&
             messageTemplate === (initial.message_template || '') &&
             accountEmail === (initialTeamsAuth.account || '') &&
             verified === !!initialTeamsAuth.verified &&
             pendingDisconnect === false;
    },
    customSaveData: () => ({
      team_id: teamId,
      channel_id: channelId,
      message_template: messageTemplate,
      teamsAuth: {
        account: accountEmail,
        verified,
        ...(pendingDisconnect ? { disconnect: true } : {})
      }
    }),
    loadDataFunction: (data) => {
      if (data.customData?.team_id !== undefined) setTeamId(data.customData.team_id);
      if (data.customData?.channel_id !== undefined) setChannelId(data.customData.channel_id);
      if (data.customData?.message_template !== undefined) setMessageTemplate(data.customData.message_template);
      const teamsAuth = data.customData?.teamsAuth || {};
      setAccountEmail(teamsAuth.account || teamsAuth.preferredUsername || '');
      setVerified(!!teamsAuth.verified);
      setPendingDisconnect(false);
      if (teamsAuth.pendingState) {
        setConnectionStatus('pending');
        setConnectionMessage(t('workflow.teams_send_message.awaiting_auth', 'Complete the Microsoft login flow in the opened tab.'));
      } else if (teamsAuth.verified) {
        setConnectionStatus('connected');
        setConnectionMessage(t('workflow.teams_send_message.connected', 'Connected to Microsoft Teams.'));
      } else {
        setConnectionStatus('disconnected');
        setConnectionMessage('');
      }
    }
  });

  const isPersistedAction = useMemo(() => (
    id && !(typeof id === 'string' && id.startsWith('temp_'))
  ), [id]);

  const handleConnect = () => {
    if (!ws) return;
    if (!isPersistedAction) {
      setConnectionMessage(t('workflow.teams_send_message.save_before_connect', 'Save the action before connecting Microsoft Teams.'));
      return;
    }

    setIsAuthorizing(true);
    setConnectionMessage('');
    setPendingDisconnect(false);
    let returnUrl = window.location.href;
    try {
      const url = new URL(returnUrl);
      url.searchParams.set('openActions', 'true');
      url.searchParams.set('exportId', String(id));
      returnUrl = url.toString();
    } catch (err) {
      // keep original returnUrl
    }
    ws.send(JSON.stringify({
      command: 'authorizeTeamsAction',
      caseid,
      exportId: id,
      account: accountEmail,
      returnUrl
    }));
  };

  const handleDisconnect = () => {
    setConnectionStatus('disconnected');
    setConnectionMessage(t('workflow.teams_send_message.disconnect_pending', 'The connection will be removed after you save these settings.'));
    setPendingDisconnect(true);
    setVerified(false);
  };

  useEffect(() => {
    const unsubscribe = subscribeToMessages((message) => {
      if (!message) return;

      if (message.command === 'authorizeTeamsActionStart') {
        const payload = message.data || {};
        if (payload.error) {
          setConnectionMessage(payload.error);
          setIsAuthorizing(false);
          return;
        }
        if (payload.exportId === id) {
          if (payload.url) {
            window.location.assign(payload.url);
          }
          setConnectionStatus('pending');
          setConnectionMessage(t('workflow.teams_send_message.awaiting_auth', 'Complete the Microsoft login flow in the opened tab.'));
          setIsAuthorizing(false);
        }
      } else if (message.command === 'teamsActionTokenResult' && (message.data?.exportId === id)) {
        const payload = message.data || {};
        if (payload.verified) {
          setConnectionStatus('connected');
          setConnectionMessage(t('workflow.teams_send_message.connected', 'Connected to Microsoft Teams.'));
          setVerified(true);
          setPendingDisconnect(false);
          if (ws && isPersistedAction) {
            ws.send(JSON.stringify(buildGetExportSettingsPayload(id, caseid)));
          }
        } else if (payload.error) {
          setConnectionStatus('disconnected');
          setConnectionMessage(payload.error);
        }
      } else if (message.command === 'verifyTeamsActionTokenResult' && (message.data?.exportId === id)) {
        const payload = message.data || {};
        setIsCheckingConnection(false);
        if (payload.verified) {
          setConnectionStatus('connected');
          setConnectionMessage(t('workflow.teams_send_message.connected', 'Connected to Microsoft Teams.'));
          setVerified(true);
          setPendingDisconnect(false);
        } else {
          setConnectionStatus('disconnected');
          setConnectionMessage(payload.error || t('workflow.teams_send_message.invalid_connection', 'Teams connection requires re-authentication.'));
          setVerified(false);
        }
      }
    });

    return unsubscribe;
  }, [subscribeToMessages, id, t, ws, isPersistedAction]);

  useEffect(() => {
    if (!ws || !isPersistedAction) {
      return;
    }
    setIsCheckingConnection(true);
    setConnectionStatus('pending');
    setConnectionMessage(t('workflow.teams_send_message.checking_status', 'Checking Teams connection...'));
    ws.send(JSON.stringify({
      command: 'verifyTeamsActionToken',
      caseid,
      exportId: id
    }));
  }, [ws, caseid, id, isPersistedAction, t]);

  const connectionChipColor = connectionStatus === 'connected'
    ? 'success'
    : connectionStatus === 'pending' || isCheckingConnection
      ? 'warning'
      : 'default';

  const connectionAlertSeverity = connectionStatus === 'connected'
    ? 'success'
    : connectionStatus === 'disconnected'
      ? 'error'
      : 'info';

  // Process tags for the dropdown/chips
  const availableTags = useMemo(() => {
    if (!tags) return [];
    if (typeof tags === 'string') {
      try {
        const parsed = JSON.parse(tags);
        return Array.isArray(parsed) ? parsed : [parsed];
      } catch (e) {
        return [];
      }
    }
    return Array.isArray(tags) ? tags : [];
  }, [tags]);

  const handleMenuOpen = (event, type) => {
    setAnchorEl(event.currentTarget);
    setMenuType(type);
  };
  const handleMainTagMenuClose = () => setAnchorEl(null);
  const handleTagSelect = (value) => {
    if (menuType === 'teamId') {
      teamIdRef.current?.insertTag(value);
    } else if (menuType === 'channelId') {
      channelIdRef.current?.insertTag(value);
    } else if (editorRef.current) {
      editorRef.current.insertTag(value);
    }
    setAnchorEl(null);
  };

  return (
    <ActionSettingsLayout
      title="Send Teams Message"
      isEnabled={isEnabled}
      onEnabledChange={setIsEnabled}
      isSaveDisabled={isSaveDisabled}
      onSave={handleSave}
      onForceRun={handleForceRun}
      forceRunLoading={forceRunLoading}
      forceRunResult={forceRunResult}
      popUpOpen={popUpOpen}
      onCloseStatusWindow={handleCloseStatusWindow}
      forceRunWarningOpen={forceRunWarningOpen}
      forceRunWarningCount={forceRunWarningCount}
      onForceRunConfirm={handleForceRunConfirm}
      onForceRunCancel={handleForceRunCancel}
      conditions={conditions}
      onConditionsChange={handleConditionsChange}
      tags={tags}
    >
      <Grid item xs={12}>
        <Card variant="outlined" sx={{ mb: 2 }}>
          <CardContent>
            <Box display="flex" alignItems="center" mb={2} gap={2}>
              <Typography variant="h6" sx={{ fontSize: { xs: '1rem', sm: '1.25rem' } }}>
                {t('workflow.teams_send_message.connection', 'Teams connection')}
              </Typography>
              <Chip
                size="small"
                color={connectionChipColor}
                label={connectionStatus === 'connected'
                  ? t('workflow.teams_send_message.status_connected', 'Connected')
                  : connectionStatus === 'pending'
                    ? t('workflow.teams_send_message.status_pending', 'Pending')
                    : t('workflow.teams_send_message.status_disconnected', 'Disconnected')}
              />
            </Box>
            <Grid container spacing={2}>
              <Grid item xs={12} md={6}>
                <TextField
                  label={t('workflow.teams_send_message.account_email', 'Microsoft account email (optional)')}
                  value={accountEmail}
                  onChange={(e) => setAccountEmail(e.target.value)}
                  fullWidth
                />
              </Grid>
              <Grid item xs={12} md={6} display="flex" alignItems="center" gap={2}>
                <Button
                  variant="contained"
                  startIcon={<LinkIcon />}
                  onClick={handleConnect}
                  disabled={isAuthorizing || isCheckingConnection || connectionStatus === 'connected'}
                >
                  {t('workflow.teams_send_message.connect', 'Connect Teams')}
                </Button>
                <Button
                  variant="outlined"
                  color="error"
                  startIcon={<LinkOffIcon />}
                  onClick={handleDisconnect}
                  disabled={connectionStatus === 'disconnected'}
                >
                  {t('workflow.teams_send_message.disconnect', 'Disconnect')}
                </Button>
              </Grid>
              {connectionMessage && (
                <Grid item xs={12}>
                  <Alert severity={connectionAlertSeverity}>
                    {connectionMessage}
                  </Alert>
                </Grid>
              )}
            </Grid>
          </CardContent>
        </Card>
      </Grid>

      {/* Teams Config */}
      <Card sx={{
        mb: 2,
        borderRadius: '14px',
        boxShadow: '0 8px 30px rgba(87,87,176,0.08)',
        border: '1px solid rgba(87,87,176,0.2)',
        overflow: 'hidden',
        background: 'linear-gradient(180deg, rgba(247,248,255,0.95) 0%, rgba(255,255,255,0.9) 100%)'
      }}>
        <Box sx={{ borderBottom: '1px solid rgba(87,87,176,0.15)' }}>
          <CardHeader
            title={<Typography variant="h6" sx={{ fontWeight: 700, letterSpacing: '-0.2px' }}>Teams IDs</Typography>}
            subheader={<Typography variant="body2" sx={{ color: 'text.secondary', maxWidth: 560 }}>
              {t(
                'workflow.teams_send_message.ids_description',
                'Provide the Team and Channel IDs so the action knows where to post. Keep them private and double-check the values.'
              )}
            </Typography>}
            sx={{ px: { xs: 2, sm: 3 }, py: { xs: 1.5, sm: 2 }, backgroundColor: 'transparent' }}
          />
        </Box>
        <CardContent sx={{ p: { xs: 2, sm: 3 } }}>
          <Grid container spacing={2}>
            <Grid item xs={12} sm={6} md={4}>
              <Box sx={{ mb: 1 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 0.5 }}>
                  <Typography variant="subtitle2" sx={{ fontWeight: 600, color: '#222' }}>
                    Team ID
                  </Typography>
                  <Tooltip
                    title={t(
                      'workflow.teams_send_message.help_team_id',
                      'Right-click the team, choose "Get link to team", and copy the groupId parameter.'
                    )}
                    arrow
                  >
                    <IconButton size="small" sx={{ p: 0 }}>
                      <InfoOutlinedIcon fontSize="inherit" />
                    </IconButton>
                  </Tooltip>
                </Box>
                <TagEditor
                    caseid={caseid}
                  ref={teamIdRef}
                  value={teamId}
                  onChange={setTeamId}
                  placeholder="Team ID"
                  singleLine
                  minHeight="40px"
                  maxRows={1}
                  onTagClick={(e) => handleMenuOpen(e, 'teamId')}
                  tags={availableTags}
                />
              </Box>
            </Grid>
            <Grid item xs={12} sm={6} md={4}>
              <Box sx={{ mb: 1 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 0.5 }}>
                  <Typography variant="subtitle2" sx={{ fontWeight: 600, color: '#222' }}>
                    Channel ID
                  </Typography>
                  <Tooltip
                    title={t(
                      'workflow.teams_send_message.help_channel_id',
                      'Open the channel, select "Get link to channel", and copy the channelId from the URL.'
                    )}
                    arrow
                  >
                    <IconButton size="small" sx={{ p: 0 }}>
                      <InfoOutlinedIcon fontSize="inherit" />
                    </IconButton>
                  </Tooltip>
                </Box>
                <TagEditor
                    caseid={caseid}
                  ref={channelIdRef}
                  value={channelId}
                  onChange={setChannelId}
                  placeholder="Channel ID"
                  singleLine
                  minHeight="40px"
                  maxRows={1}
                  onTagClick={(e) => handleMenuOpen(e, 'channelId')}
                  tags={availableTags}
                />
              </Box>
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      {/* Message Template */}
      <Card sx={{ 
        borderRadius: '12px', 
        boxShadow: '0 4px 20px rgba(0,0,0,0.04)', 
        border: '1px solid #5757b010', 
        overflow: 'hidden' 
      }}>
        <CardHeader
          title={<Typography sx={{ fontWeight: 700, color: '#444', fontSize: { xs: '1rem', sm: '1.25rem' } }}>Message Template</Typography>}
          sx={{ borderBottom: '1px solid #f0f0f8', backgroundColor: '#fafbff', p: { xs: 1.5, sm: 2 } }}
        />
        <CardContent sx={{ p: { xs: 1.5, sm: 2 } }}>
          <Box sx={{
            border: '1px solid #d0d0e8',
            borderRadius: '12px',
            backgroundColor: '#fff',
            p: 0,
            overflow: 'hidden',
            '&:hover': {
              borderColor: '#5757b0',
              boxShadow: '0 4px 12px rgba(87, 87, 176, 0.08)',
            },
            transition: 'all 0.2s ease',
          }}>
            <TagEditor
                    caseid={caseid}
              ref={editorRef}
              value={messageTemplate}
              onChange={setMessageTemplate}
              placeholder="Write Teams message template. You can use tags like {filename}..."
              onTagClick={handleMenuOpen}
              tags={availableTags}
            />
            
            <Box sx={{ 
              p: 2, 
              display: 'flex', 
              flexWrap: 'wrap', 
              gap: 1,
              backgroundColor: '#f8f9ff',
              borderTop: '1px solid #eaeaf0',
            }}>
              <Typography sx={{ fontWeight: 600, color: '#5757b0', fontSize: '0.85rem', mr: 1 }}>Quick Tags:</Typography>
              {availableTags && availableTags.length > 0 ? (
                availableTags.slice(0, 6).map(tag => (
                  <Chip
                    key={tag.variable}
                    label={tag.variable}
                    onClick={() => {
                        // Ensure we prevent default to avoid focus issues
                        if (editorRef.current) {
                            editorRef.current.focus();
                            // Small delay to ensure focus is applied before insertion
                            setTimeout(() => {
                                editorRef.current.insertTag(tag.variable);
                            }, 10);
                        }
                    }}
                    size="small"
                    sx={{
                      backgroundColor: '#f0f0ff',
                      color: '#5757b0',
                      borderColor: '#5757b030',
                      border: '1px solid',
                      fontWeight: 600,
                      transition: 'all 0.2s ease',
                    }}
                  />
                ))
              ) : (
                <Typography variant="caption" sx={{ color: '#999', fontStyle: 'italic' }}>
                  No tags available
                </Typography>
              )}
            </Box>
          </Box>
        </CardContent>
      </Card>
      
      <TagsDropdown
        tags={availableTags}
        handleTagSelect={handleTagSelect}
        anchorEl={anchorEl}
        setAnchorEl={handleMainTagMenuClose}
        menuType={menuType}
      />
    </ActionSettingsLayout>
  );
};

export default React.memo(ActionsSendTeamsMessage);

